import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import api from "@/api";
import MenuManager from "@/components/MenuManager";
import OutletManager from "@/components/OutletManager";
import ProductManager from "@/components/ProductManager";
import JournalManager from "@/components/JournalManager";
import FranchiseManager from "@/components/FranchiseManager";

const ENQ_STATUSES = ["new", "contacted", "closed"];
const ORD_STATUSES = ["paid", "confirmed", "delivered", "awaiting_payment"];

const inputCls = "w-full rounded-xl border border-clayline bg-cream px-4 py-3 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:ring-2 focus:ring-terracotta/60";

const StatusSelect = ({ value, options, onChange, tid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={tid}
    className="rounded-full border border-clayline bg-cream px-3 py-1.5 font-mono text-xs uppercase tracking-wider focus:outline-none"
  >
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem("ct_token"));
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, o] = await Promise.all([api.get("/admin/enquiries"), api.get("/admin/orders")]);
      setEnquiries(e.data);
      setOrders(o.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ct_token");
        setToken(null);
      } else {
        toast.error("Failed to load dashboard data.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("ct_token", data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    }
  };

  const logout = () => {
    localStorage.removeItem("ct_token");
    setToken(null);
  };

  const setEnqStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const setOrdStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    setOrders((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-forest text-cream flex items-center justify-center px-5 grain relative" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-espresso border border-cream/10 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-2">
            <img src="/logo.png" alt="Chai Times logo" className="w-12 h-12 rounded-full object-contain" />
            <span className="font-lobster text-3xl">Chai <span className="text-gold">Times</span></span>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cream/50 mb-7">Admin Console</p>
          <input
            type="email" required placeholder="Admin email"
            value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            className="w-full rounded-xl border border-cream/20 bg-forest px-4 py-3 text-cream placeholder:text-cream/35 focus:outline-none focus:ring-2 focus:ring-terracotta/60"
            data-testid="admin-email-input"
          />
          <input
            type="password" required placeholder="Password"
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            className="w-full rounded-xl border border-cream/20 bg-forest px-4 py-3 mt-4 text-cream placeholder:text-cream/35 focus:outline-none focus:ring-2 focus:ring-terracotta/60"
            data-testid="admin-password-input"
          />
          {error && <p className="text-red-400 text-sm mt-3" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-btn" className="mt-6 w-full rounded-full px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] bg-terracotta text-cream hover:bg-gold hover:text-charcoal transition-colors">
            Sign In
          </button>
          <Link to="/" className="block text-center mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/40 hover:text-gold transition-colors" data-testid="admin-back-home">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand/60" data-testid="admin-dashboard">
      <header className="bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Chai Times logo" className="w-9 h-9 rounded-full object-contain" />
            <span className="font-lobster text-2xl">Chai Times — <span className="text-gold">Admin</span></span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh-btn" className="p-2 hover:text-gold transition-colors" aria-label="Refresh"><RefreshCw size={18} /></button>
            <button onClick={logout} data-testid="admin-logout-btn" className="rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-cream/25 hover:border-gold hover:text-gold transition-colors inline-flex items-center gap-2">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="bg-cream border border-clayline rounded-2xl p-5" data-testid="stat-total-enquiries">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">Enquiries</p>
            <p className="font-serif font-black text-4xl mt-2">{enquiries.length}</p>
          </div>
          <div className="bg-cream border border-clayline rounded-2xl p-5" data-testid="stat-new-enquiries">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">New</p>
            <p className="font-serif font-black text-4xl mt-2 text-terracotta">{enquiries.filter((e) => e.status === "new").length}</p>
          </div>
          <div className="bg-cream border border-clayline rounded-2xl p-5" data-testid="stat-total-orders">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">Orders</p>
            <p className="font-serif font-black text-4xl mt-2">{orders.length}</p>
          </div>
          <div className="bg-cream border border-clayline rounded-2xl p-5" data-testid="stat-order-revenue">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">Order Value</p>
            <p className="font-serif font-black text-4xl mt-2">₹{orders.reduce((s, o) => s + (o.total || 0), 0)}</p>
          </div>
        </div>

        <div className="flex gap-3 mb-6 flex-wrap">
          {["enquiries", "orders", "menu", "outlets", "shop", "journal", "franchise"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                tab === t ? "bg-forest text-cream" : "border border-clayline text-charcoal/60 hover:border-terracotta"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading && <p className="font-mono text-sm text-charcoal/50" data-testid="admin-loading">Loading…</p>}

        {tab === "menu" && <MenuManager />}

        {tab === "outlets" && <OutletManager />}

        {tab === "shop" && <ProductManager />}

        {tab === "journal" && <JournalManager />}

        {tab === "franchise" && <FranchiseManager />}

        {!loading && tab === "enquiries" && (
          <div className="space-y-4" data-testid="enquiries-list">
            {enquiries.length === 0 && <p className="font-mono text-sm text-charcoal/50" data-testid="enquiries-empty">No enquiries yet.</p>}
            {enquiries.map((enq) => (
              <div key={enq.id} className="bg-cream border border-clayline rounded-2xl p-6" data-testid={`enquiry-row-${enq.id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif font-bold text-xl">{enq.name}</h3>
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full ${enq.type === "franchise" ? "bg-terracotta text-cream" : "bg-sand text-charcoal"}`}>
                        {enq.type}
                      </span>
                    </div>
                    <p className="font-mono text-xs text-charcoal/55 mt-2">{enq.phone}{enq.email ? ` · ${enq.email}` : ""}{enq.city ? ` · ${enq.city}` : ""}{enq.budget ? ` · Budget ₹${enq.budget}L` : ""}</p>
                    {enq.message && <p className="text-sm text-charcoal/70 mt-3 leading-relaxed">{enq.message}</p>}
                    <p className="font-mono text-[10px] text-charcoal/40 mt-3">{new Date(enq.created_at).toLocaleString()}</p>
                  </div>
                  <StatusSelect value={enq.status} options={ENQ_STATUSES} onChange={(s) => setEnqStatus(enq.id, s)} tid={`enquiry-status-${enq.id.slice(0, 8)}`} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "orders" && (
          <div className="space-y-4" data-testid="orders-list">
            {orders.length === 0 && <p className="font-mono text-sm text-charcoal/50" data-testid="orders-empty">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} className="bg-cream border border-clayline rounded-2xl p-6" data-testid={`order-row-${o.id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-serif font-bold text-xl">{o.customer?.name} <span className="font-mono text-sm text-terracotta">₹{o.total}</span></h3>
                    <p className="font-mono text-xs text-charcoal/55 mt-2">{o.customer?.phone} · {[o.customer?.address, o.customer?.city, o.customer?.state, o.customer?.pincode].filter(Boolean).join(", ")}</p>
                    <ul className="text-sm text-charcoal/70 mt-3 space-y-1">
                      {o.items?.map((i, idx) => <li key={idx}>— {i.name} × {i.qty} (₹{i.price * i.qty})</li>)}
                    </ul>
                    <p className="font-mono text-[10px] text-charcoal/40 mt-3">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  <StatusSelect value={o.status} options={ORD_STATUSES} onChange={(s) => setOrdStatus(o.id, s)} tid={`order-status-${o.id.slice(0, 8)}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
